import React, { useEffect, useState } from "react";
import { Form, Input, Button, Upload, Card, Row, Col, message, Select, Modal, Space } from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import axios from "axios";
import v1 from '../assets/icons/v1.svg';
import v2 from '../assets/icons/v2.svg';
import v3 from '../assets/icons/v3.svg';
import v4 from '../assets/icons/v4.svg';
import v5 from '../assets/icons/v5.svg';
import v6 from '../assets/icons/v6.svg';
import v7 from '../assets/icons/v7.svg';
import v8 from '../assets/icons/v8.svg';
import v9 from '../assets/icons/v9.svg';
import e1 from '../assets/Ellipse/e1.svg';
import e2 from '../assets/Ellipse/e2.svg';
import e3 from '../assets/Ellipse/e3.svg';
import e4 from '../assets/Ellipse/e4.svg';
import e5 from '../assets/Ellipse/e5.svg';
import e6 from '../assets/Ellipse/e6.svg';
import e7 from '../assets/Ellipse/e7.svg';
import e8 from '../assets/Ellipse/e8.svg';
import e9 from '../assets/Ellipse/e9.svg';

const { TextArea } = Input;

const iconMap = {
  v1: { src: v1, name: 'Birthday' },
  v2: { src: v2, name: 'Kitty Party' },
  v3: { src: v3, name: 'VR/Games' },
  v4: { src: v4, name: 'Restaurant' },
  v5: { src: v5, name: 'Poolside' },
  v6: { src: v6, name: 'Baby Shower' },
  v7: { src: v7, name: 'Naming' },
  v8: { src: v8, name: 'Anniversary' },
  v9: { src: v9, name: 'School Tour' },
};

const ellipseMap = {
  e1: { src: e1, name: '1', color: '#FFB6C1' },
  e2: { src: e2, name: '2', color: '#87CEEB' },
  e3: { src: e3, name: '3', color: '#90EE90' },
  e4: { src: e4, name: '4', color: '#FFD700' },
  e5: { src: e5, name: '5', color: '#DDA0DD' },
  e6: { src: e6, name: '6', color: '#FFA500' },
  e7: { src: e7, name: '7', color: '#FF6B6B' },
  e8: { src: e8, name: '8', color: '#40E0D0' },
  e9: { src: e9, name: '9', color: '#6366F1' },
};

const ServiceEditPage = () => {
  const [form] = Form.useForm();
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState("");
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm] = Form.useForm();
  const [categoryImage, setCategoryImage] = useState(null);
  const [selectedIcon, setSelectedIcon] = useState('v1');
  const [selectedEllipse, setSelectedEllipse] = useState('e1');

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      setToken(storedToken);
    } else {
      message.error("Authentication token not found. Please login.");
    }
  }, []);

  useEffect(() => {
    if (!token) return;

    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/service-categories`)
      .then((res) => setCategories(res.data || []))
      .catch((error) => console.error("Failed to fetch categories:", error));

    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/services`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data) {
          form.setFieldsValue({ heading: res.data.heading });
          const formattedServices = res.data.services.map(service => ({
            ...service,
            category: service.category?._id || null,
            images: service.images || [],
            previews: service.images?.map(img => img.src) || [],
            newImages: [],
          }));
          setServices(formattedServices || []);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch services data:", error);
        message.error("Failed to fetch services data.");
      });
  }, [form, token]);

  const onFinish = async (values) => {
    setLoading(true);

    const servicesToSubmit = services.map(service => {
      const allImages = [
        ...service.images.map(img => ({
          src: img.src,
          cloudinaryPublicId: img.cloudinaryPublicId,
          alt: img.alt || '',
        })),
        ...service.newImages.map(base64 => ({ data: base64, alt: service.title })),
      ];

      return {
        _id: service._id,
        title: service.title,
        description: service.description,
        shortDescription: service.shortDescription || '',
        category: service.category || null,
        images: allImages,
      };
    });

    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/services`, {
        heading: values.heading,
        services: servicesToSubmit,
      }, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      message.success("Services updated successfully!");
    } catch (e) {
      console.error("Update failed:", e);
      message.error("Update failed: " + (e.response?.data?.error || e.message));
    }
    setLoading(false);
  };

  const handleServiceChange = (idx, key, value) => {
    const newServices = [...services];
    newServices[idx][key] = value;
    setServices(newServices);
  };

  const handleImageChange = (idx, file) => {
    if (!file) return false;

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const newServices = [...services];
      newServices[idx].newImages = [...(newServices[idx].newImages || []), reader.result];
      newServices[idx].previews = [...(newServices[idx].previews || []), reader.result];
      setServices(newServices);
    };
    reader.onerror = (error) => {
      console.error("Error reading file:", error);
      message.error("Failed to read image file.");
    };
    return false;
  };

  const removeImage = (serviceIdx, imageIdx) => {
    const newServices = [...services];
    newServices[serviceIdx].images.splice(imageIdx, 1);
    newServices[serviceIdx].previews.splice(imageIdx, 1);
    setServices(newServices);
  };

  const openCategoryModal = (category = null) => {
    setEditingCategory(category);
    setCategoryImage(null);
    if (category) {
      categoryForm.setFieldsValue({ 
        name: category.name, 
        description: category.description || '',
        icon: category.icon || 'v1',
        ellipse: category.ellipse || 'e1',
        order: category.order 
      });
      setSelectedIcon(category.icon || 'v1');
      setSelectedEllipse(category.ellipse || 'e1');
    } else {
      categoryForm.resetFields();
      categoryForm.setFieldsValue({ icon: 'v1', ellipse: 'e1' });
      setSelectedIcon('v1');
      setSelectedEllipse('e1');
    }
    setCategoryModalVisible(true);
  };

  const handleCategoryImageChange = (file) => {
    if (!file) return false;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => setCategoryImage(reader.result);
    return false;
  };

  const handleCategorySubmit = async () => {
    try {
      const values = await categoryForm.validateFields();
      const payload = { ...values };
      
      if (categoryImage) {
        payload.image = { data: categoryImage, alt: values.name };
      } else if (editingCategory?.image) {
        payload.image = editingCategory.image;
      }

      if (editingCategory) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/service-categories/${editingCategory._id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success("Category updated successfully!");
      } else {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/service-categories`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success("Category created successfully!");
      }
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/service-categories`);
      setCategories(res.data || []);
      setCategoryModalVisible(false);
    } catch (error) {
      message.error("Failed to save category");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/service-categories/${categoryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Category deleted successfully!");
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/service-categories`);
      setCategories(res.data || []);
    } catch (error) {
      message.error("Failed to delete category");
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Card title="Heading">
        <Form.Item name="heading" label="Heading" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
      </Card>

      <Card title="Service Categories" style={{ marginTop: 24 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openCategoryModal()}>
            Add Category
          </Button>
          <Row gutter={[16, 16]}>
            {categories.map((cat) => (
              <Col span={8} key={cat._id}>
                <Card
                  size="small"
                  hoverable
                  style={{ borderRadius: '12px', overflow: 'hidden' }}
                  cover={
                    <div style={{ 
                      height: 140, 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      position: 'relative'
                    }}>
                      {cat.image?.src ? (
                        <img 
                          alt={cat.name} 
                          src={cat.image.src} 
                          style={{ 
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            position: 'absolute',
                            top: 0,
                            left: 0
                          }} 
                        />
                      ) : (
                        <div style={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '50%',
                          backgroundImage: `url(${ellipseMap[cat.ellipse || 'e1']?.src})`,
                          backgroundSize: 'cover',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          zIndex: 1
                        }}>
                          <img 
                            src={iconMap[cat.icon || 'v1']?.src} 
                            alt={cat.name}
                            style={{ width: '45px', height: '45px' }}
                          />
                        </div>
                      )}
                    </div>
                  }
                  actions={[
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => openCategoryModal(cat)}
                      type="text"
                    >
                      Edit
                    </Button>,
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteCategory(cat._id)}
                      type="text"
                    >
                      Delete
                    </Button>
                  ]}
                >
                  <Card.Meta 
                    title={<span style={{ fontSize: '16px', fontWeight: 'bold' }}>{cat.name}</span>} 
                    description={
                      <div>
                        <div style={{ marginBottom: 8, color: '#666' }}>
                          {cat.description || 'No description'}
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ 
                            fontSize: '11px', 
                            background: '#f0f0f0', 
                            padding: '2px 8px', 
                            borderRadius: '4px',
                            color: '#666'
                          }}>
                            Icon: {iconMap[cat.icon || 'v1']?.name}
                          </span>
                          <span style={{ 
                            fontSize: '11px', 
                            background: '#f0f0f0', 
                            padding: '2px 8px', 
                            borderRadius: '4px',
                            color: '#666'
                          }}>
                            BG: {ellipseMap[cat.ellipse || 'e1']?.name}
                          </span>
                          <span style={{ 
                            fontSize: '11px', 
                            background: '#e6f7ff', 
                            padding: '2px 8px', 
                            borderRadius: '4px',
                            color: '#1890ff'
                          }}>
                            Order: {cat.order}
                          </span>
                        </div>
                      </div>
                    } 
                  />
                </Card>
              </Col>
            ))}
          </Row>
        </Space>
      </Card>

      <Card title="Services" style={{ marginTop: 24 }}>
        <Row gutter={16}>
          {services.map((service, idx) => (
            <Col span={12} key={idx}>
              <Card
                title={`Service ${idx + 1}`}
                style={{ marginBottom: 16 }}
                extra={
                  <Button
                    danger
                    onClick={() => setServices(services.filter((_, i) => i !== idx))}
                  >
                    Delete
                  </Button>
                }
              >
                <Input
                  value={service.title}
                  onChange={(e) => handleServiceChange(idx, "title", e.target.value)}
                  placeholder="Title"
                  style={{ marginBottom: 8 }}
                />
                <Input
                  value={service.shortDescription}
                  onChange={(e) => handleServiceChange(idx, "shortDescription", e.target.value)}
                  placeholder="Short Description"
                  style={{ marginBottom: 8 }}
                />
                <TextArea
                  value={service.description}
                  onChange={(e) => handleServiceChange(idx, "description", e.target.value)}
                  rows={3}
                  placeholder="Full Description"
                  style={{ marginBottom: 8 }}
                />
                <Select
                  value={service.category}
                  onChange={(value) => handleServiceChange(idx, "category", value)}
                  placeholder="Select Category"
                  style={{ width: '100%', marginBottom: 8 }}
                  allowClear
                >
                  {categories.map((cat) => (
                    <Select.Option key={cat._id} value={cat._id}>
                      {cat.name}
                    </Select.Option>
                  ))}
                </Select>
                <Upload
                  beforeUpload={(file) => handleImageChange(idx, file)}
                  showUploadList={false}
                  multiple
                >
                  <Button icon={<PlusOutlined />}>Add Images</Button>
                </Upload>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  {service.previews?.map((preview, imgIdx) => (
                    <div key={imgIdx} style={{ position: 'relative' }}>
                      <img
                        src={preview}
                        alt="preview"
                        style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }}
                      />
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        style={{ position: 'absolute', top: 0, right: 0 }}
                        onClick={() => removeImage(idx, imgIdx)}
                      />
                    </div>
                  ))}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
        <Button
          type="dashed"
          block
          icon={<PlusOutlined />}
          onClick={() => setServices([...services, { title: "", description: "", shortDescription: "", category: null, images: [], previews: [], newImages: [] }])}
        >
          Add Service
        </Button>
      </Card>

      <Button type="primary" htmlType="submit" loading={loading} style={{ marginTop: 24 }}>
        Save Changes
      </Button>

      <Modal
        title={editingCategory ? "Edit Category" : "Add Category"}
        open={categoryModalVisible}
        onOk={handleCategorySubmit}
        onCancel={() => setCategoryModalVisible(false)}
        width={700}
      >
        <Form form={categoryForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="Category Name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="order" label="Display Order" initialValue={0}>
                <Input type="number" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <TextArea rows={3} placeholder="Brief description of this category" />
          </Form.Item>

          {/* Live Preview Card */}
          <Card 
            title="Live Preview" 
            size="small" 
            style={{ 
              marginBottom: 24, 
              background: '#f5f5f5',
              border: '2px dashed #d9d9d9'
            }}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              padding: '20px'
            }}>
              <div style={{
                width: '200px',
                height: '200px',
                borderRadius: '20px',
                border: '2px solid #f87171',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                background: '#fff',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}>
                <div style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  backgroundImage: `url(${ellipseMap[selectedEllipse].src})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <img 
                    src={iconMap[selectedIcon].src} 
                    alt="icon preview" 
                    style={{ width: '60px', height: '60px' }}
                  />
                </div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  color: '#333'
                }}>
                  {categoryForm.getFieldValue('name') || 'Category Name'}
                </div>
              </div>
            </div>
          </Card>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="icon" label="Select Icon" initialValue="v1">
                <Select 
                  onChange={(value) => setSelectedIcon(value)}
                  optionLabelProp="label"
                >
                  {Object.entries(iconMap).map(([key, { src, name }]) => (
                    <Select.Option key={key} value={key} label={name}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '8px',
                          background: '#f0f0f0',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}>
                          <img src={src} alt={name} style={{ width: '28px', height: '28px' }} />
                        </div>
                        <span style={{ fontWeight: '500' }}>{name}</span>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="ellipse" label="Select Background" initialValue="e1">
                <Select 
                  onChange={(value) => setSelectedEllipse(value)}
                  optionLabelProp="label"
                >
                  {Object.entries(ellipseMap).map(([key, { src, name, color }]) => (
                    <Select.Option key={key} value={key} label={name}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundImage: `url(${src})`,
                          backgroundSize: 'cover',
                          border: '2px solid #e0e0e0'
                        }} />
                        <span style={{ fontWeight: '500' }}>{name}</span>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Category Image (for Services Page)">
            <Upload
              beforeUpload={handleCategoryImageChange}
              showUploadList={false}
            >
              <Button icon={<PlusOutlined />}>Upload Image</Button>
            </Upload>
            {(categoryImage || editingCategory?.image?.src) && (
              <img
                src={categoryImage || editingCategory?.image?.src}
                alt="category"
                style={{ width: 100, height: 100, objectFit: 'cover', marginTop: 8, borderRadius: 8 }}
              />
            )}
          </Form.Item>
        </Form>
      </Modal>
    </Form>
  );
};

export default ServiceEditPage;
